import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, Flame, List, PartyPopper, Pencil, Plus } from 'lucide-react';

import ReservaModal from './ReservaModal';
import EspacosModal from './EspacosModal';
import ReservasCalendario, { todaySaoPaulo } from './ReservasCalendario';
import api from '../../services/api';
import { LoadingState, PageHeader, PageLayout, SearchField, SummaryCard, SummaryGrid } from '../../components/ui/PageLayout';
import { formatDateBR, normalizeSearch } from '../../utils/formatters';

const AgendaPage = () => {
  const [reservas, setReservas] = useState([]);
  const [espacos, setEspacos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEspacosModalOpen, setIsEspacosModalOpen] = useState(false);
  const [moradores, setMoradores] = useState([]);
  const [filtroHoje, setFiltroHoje] = useState(false);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [viewMode, setViewMode] = useState('calendario');
  const [dataInicial, setDataInicial] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resReservas, resMoradores, resEspacos] = await Promise.all([
        api.get('/agenda/'),
        api.get('/agenda/moradores'),
        api.get('/agenda/espacos')
      ]);
      setReservas(resReservas.data);
      setMoradores(resMoradores.data);
      setEspacos(resEspacos.data);
    } catch (error) {
      console.error('Erro ao buscar dados da agenda:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  const hojeISO = todaySaoPaulo();
  const horarioAtual = currentTime.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const totalReservas = reservas.length;
  const reservasAtivas = reservas.filter((reserva) => reserva.status === 'Reservado' && reserva.data >= hojeISO).length;
  const contadorHoje = reservas.filter((reserva) => reserva.data === hojeISO && reserva.status === 'Reservado').length;

  const reservasExibidas = useMemo(() => {
    const termoBusca = normalizeSearch(searchTerm);
    let listaBase = reservas;

    if (statusFilter === 'ativas') {
      listaBase = listaBase.filter((reserva) => reserva.status === 'Reservado' && reserva.data >= hojeISO);
    }

    if (filtroHoje) {
      listaBase = listaBase.filter((reserva) => reserva.data === hojeISO && reserva.status === 'Reservado');
    }

    return listaBase.filter((reserva) =>
      [reserva.area, reserva.nome_morador, reserva.status].some((valor) =>
        normalizeSearch(valor).includes(termoBusca)
      )
    );
  }, [filtroHoje, hojeISO, reservas, searchTerm, statusFilter]);

  const handleCancelar = async (id) => {
    if (window.confirm('Deseja realmente cancelar esta reserva?')) {
      try {
        await api.delete(`/agenda/${id}`);
        fetchData();
      } catch (error) {
        alert('Erro ao cancelar reserva.');
      }
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Reservas"
        description="Gerencie as reservas de áreas comuns"
        actions={(
          <>
            <button
              onClick={() => setIsEspacosModalOpen(true)}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 font-bold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              <Pencil size={18} /> Editar
            </button>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 font-mono text-sm font-bold text-slate-700 shadow-sm">
              <Clock size={18} className="text-slate-500" />
              <span>{horarioAtual}</span>
            </div>
            <button
              onClick={() => { setDataInicial(''); setIsModalOpen(true); }}
              className="flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2 font-bold text-white shadow-lg transition hover:bg-slate-700"
            >
              <Plus size={20} /> Nova Reserva
            </button>
          </>
        )}
      />

      <SummaryGrid>
        <SummaryCard
          title="Total de Reservas"
          value={totalReservas}
          icon={<Calendar size={24} />}
          active={statusFilter === 'todos' && !filtroHoje}
          onClick={() => {
            setStatusFilter('todos');
            setFiltroHoje(false);
          }}
        />
        <SummaryCard
          title="Reservas Ativas"
          value={reservasAtivas}
          icon={<PartyPopper size={24} />}
          active={statusFilter === 'ativas'}
          onClick={() => {
            setStatusFilter((current) => (current === 'ativas' ? 'todos' : 'ativas'));
            setFiltroHoje(false);
          }}
        />
        <SummaryCard
          title={filtroHoje ? 'Filtrando: Hoje' : 'Reservas Hoje'}
          value={contadorHoje}
          icon={<Flame size={24} className={filtroHoje ? 'text-orange-500' : ''} />}
          active={filtroHoje}
          onClick={() => {
            setFiltroHoje((current) => !current);
            setStatusFilter('todos');
          }}
        />
      </SummaryGrid>

      <SearchField
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Buscar reserva por área, morador ou status..."
      />

      <div role="group" aria-label="Modo de visualização" className="flex w-fit gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {[{ value: 'lista', label: 'Lista', Icon: List }, { value: 'calendario', label: 'Calendário', Icon: Calendar }].map(({ value, label, Icon }) => <button key={value} type="button" aria-pressed={viewMode === value} onClick={() => setViewMode(value)} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${viewMode === value ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-50'}`}><Icon size={17} />{label}</button>)}
      </div>

      {viewMode === 'calendario' ? <ReservasCalendario reservas={reservasExibidas} loading={loading} onCancelar={handleCancelar} onNovaReserva={(data) => { setDataInicial(data); setIsModalOpen(true); }} /> : <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full border-collapse text-left">
            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-400">
              <tr>
                <th className="px-6 py-4">Área</th>
                <th className="px-6 py-4">Morador</th>
                <th className="px-6 py-4">Data</th>
                <th className="px-6 py-4">Horário</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="p-0">
                    <LoadingState message="Carregando reservas..." />
                  </td>
                </tr>
              ) : reservasExibidas.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-10 text-center italic text-gray-400">
                    Nenhuma reserva encontrada para este filtro.
                  </td>
                </tr>
              ) : (
                reservasExibidas.map((reserva) => (
                  <tr key={reserva.id} className="group transition-colors hover:bg-gray-50/50">
                    <td className="flex items-center gap-2 px-6 py-4 font-medium text-slate-700">
                      <PartyPopper size={16} className="text-gray-400" />
                      {reserva.area}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{reserva.nome_morador}</td>
                    <td className="px-6 py-4 text-gray-600">{formatDateBR(`${reserva.data}T00:00:00`)}</td>
                    <td className="px-6 py-4 font-mono text-sm text-gray-600">
                      {reserva.hora_inicio} - {reserva.hora_fim}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`flex w-fit items-center gap-1 rounded-full px-2 py-1 text-xs font-bold ${
                          reserva.status === 'Reservado' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            reserva.status === 'Reservado' ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                        />
                        {reserva.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleCancelar(reserva.id)}
                        className="text-sm font-medium text-red-500 transition-colors hover:text-red-700"
                      >
                        Cancelar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>}

      <ReservaModal
        dataInicial={dataInicial}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        moradores={moradores}
        refreshData={fetchData}
        reservasExistentes={reservas}
        espacos={espacos}
      />
      <EspacosModal
        isOpen={isEspacosModalOpen}
        onClose={() => setIsEspacosModalOpen(false)}
        espacos={espacos}
        refreshData={fetchData}
      />
    </PageLayout>
  );
};

export default AgendaPage;
